import React, { useState, useEffect, useRef } from 'react';
import { storage, db, app } from '@/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { useConfig } from '@/context/ConfigContext';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useUser } from '@/context/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, LogOut, AlertCircle, KeyRound } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { validatePhoneNumber } from '@/lib/profilesSync';
import { seedDismissedForNewProfile } from '@/lib/alertsSync';
import { PlanUpgradeModal } from '@/components/PlanUpgradeModal';
import { generateSalt, hashPin } from '@/lib/pinCrypto';
import { revokeTrustedDevices, removeLocalDeviceToken } from '@/lib/trustedDeviceSync';

const rutappLogo = "/images/rutapp-logo.png.png";

// Referencia única de las callables suspendProfile/restoreProfile/deleteProfileNow: no se recrean en cada render/click.
const functions = getFunctions(app);
const suspendProfileCallable = httpsCallable(functions, 'suspendProfile');
const restoreProfileCallable = httpsCallable(functions, 'restoreProfile');
const deleteProfileNowCallable = httpsCallable(functions, 'deleteProfileNow');
const inviteWorkerCallable = httpsCallable(functions, 'inviteWorkerWithProfile');
const promoteUserToAdminCallable = httpsCallable(functions, 'promoteUserToAdmin');
const demoteAdminToUserCallable = httpsCallable(functions, 'demoteAdminToUser');
const listWorkspaceMembersCallable = httpsCallable(functions, 'listWorkspaceMembers');



/**
 * Valida el formato de un PIN: solo dígitos, 4 a 6 caracteres.
 */
function validatePinFormat(pin) {
  if (!/^\d{4,6}$/.test(pin || '')) {
    return { valid: false, message: 'El PIN debe tener entre 4 y 6 dígitos numéricos.' };
  }
  return { valid: true, message: '' };
}

/**
 * Formatea deletionScheduledAt (Firestore Timestamp) de forma segura.
 * Si el valor está ausente o no tiene .toDate(), devuelve un texto neutro
 * en vez de lanzar un error.
 */
function formatDeletionDate(deletionScheduledAt) {
  if (
    deletionScheduledAt &&
    typeof deletionScheduledAt.toDate === 'function'
  ) {
    return deletionScheduledAt
      .toDate()
      .toLocaleDateString('es-ES');
  }

  return 'fecha no disponible';
}


export function SettingsModal({ isOpen, onClose, workspaceId, userRole, onConfigureProfilePin }) {
  const {
    config,
    updateConfig,
    updateRouteStatusConfig,
    updateProfilesConfig,
    updateWorkspaceMeta,
    adminPinHash,
    updateAdminSecurityConfig,
    removeAdminPinConfig,
  } = useConfig();

  const { handleLogout: authLogout } = useAuth();
  const { unlockProfile } = useUser();
  const {
  permissions,
  isFree,
  isPremiumPersonal,
  isPremiumPro,
  maxProfiles,
} = useSubscription();
  const { toast } = useToast();
  const [phoneValidationErrors, setPhoneValidationErrors] = useState({});
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

  // Perfil activo: status === 'active' o ausencia de status (legacy).
  // pending_deletion y deleting no se editan como perfiles normales.
  const isActiveProfile = (profile) => (profile.status ?? 'active') === 'active';

  // Estados para otros campos
  const [companyName, setCompanyName] = useState(config.companyName);
  const [profiles, setProfiles] = useState(() => config.profiles.filter(isActiveProfile));

  // Perfiles pendientes de eliminación: derivado de config.profiles, solo lectura.
   // No se mantiene como copia editable en useState para no arriesgar sobrescrituras.
  const pendingProfiles = React.useMemo(
    () => config.profiles.filter(p => p.status === "pending_deletion" || p.status === "deleting"),
    [config.profiles]
  );
   // Perfiles bloqueados por plan: status === 'locked_by_plan'
   const lockedByPlanProfiles = React.useMemo(
     () => config.profiles.filter(p => p.status === 'locked_by_plan'),
     [config.profiles]
   );


   const [colorCodeEnabled, setColorCodeEnabled] = useState(config.colorCodeEnabled ?? true);
   const [shareWhatsAppAccess, setShareWhatsAppAccess] = useState(config.shareWhatsAppAccess ?? true);
   const [shareFleetAlerts, setShareFleetAlerts] = useState(config.shareFleetAlerts ?? true);
   const [showAllProfilesInfo, setShowAllProfilesInfo] = useState(config.showAllProfilesInfo ?? false);
   const [allowCrossProfileClientsConsultation, setAllowCrossProfileClientsConsultation] = useState(config.allowCrossProfileClientsConsultation ?? false);

   // Estados DRAFT para Estados de Ruta (modo formulario)
  const [draftCount, setDraftCount] = useState('4');
   const [draftColors, setDraftColors] = useState({
     go: 'green',
     salimos: 'blue',
     volvemos: 'yellow',
     fin: 'red',
   });

  const [draftLabels, setDraftLabels] = useState(['Go', 'Salimos', 'Volvemos', 'Fin']);

  // Estado para subida de imagen
  const [uploadingIndex, setUploadingIndex] = useState(null);

  // Set de profileIds cuyas imágenes se borrarán de Storage solo al guardar
  const [pendingImageDeletions, setPendingImageDeletions] = useState(new Set());

   // Estado para confirmar suspensión de perfil (7 días recuperable vía backend)
   const [profileIdToSuspend, setProfileIdToSuspend] = useState(null);
   const [isSuspending, setIsSuspending] = useState(false);
   const [suspendError, setSuspendError] = useState('');
   // Paso de la doble confirmación antes de suspender: null | 'first' | 'second'
   const [suspendConfirmStep, setSuspendConfirmStep] = useState(null);

     // Estado para recuperar un perfil pending_deletion (por fila, no bloquea el modal)
     const [restoringProfileId, setRestoringProfileId] = useState(null);
     const [restoreErrors, setRestoreErrors] = useState({});

     // Estado para confirmar recuperación antes de ejecutarla
     const [profileIdToRestore, setProfileIdToRestore] = useState(null);

     // Estado para eliminar un perfil pending_deletion de forma permanente e irreversible
    const [profileIdToDeleteNow, setProfileIdToDeleteNow] = useState(null);
    const [isDeletingNow, setIsDeletingNow] = useState(false);
    const [deleteNowError, setDeleteNowError] = useState('');

    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);



   // Estado para el formulario de PIN de administrador (crear/cambiar)
   const [adminNewPinInput, setAdminNewPinInput] = useState('');
   const [adminConfirmPinInput, setAdminConfirmPinInput] = useState('');
   const [isSavingAdminPin, setIsSavingAdminPin] = useState(false);

   // Estado para confirmar y ejecutar la eliminación del PIN de administrador
   const [showRemoveAdminPinConfirm, setShowRemoveAdminPinConfirm] = useState(false);
   const [isRemovingAdminPin, setIsRemovingAdminPin] = useState(false);

     // ProfileId cuyo PIN se va a restablecer (confirmación)
     const [profileIdToResetPin, setProfileIdToResetPin] = useState(null);

    // Estados para vincular un trabajador
    const [inviteEmail, setInviteEmail] = useState('');
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState('');
    const [inviteSuccess, setInviteSuccess] = useState('');

    // Estados para listWorkspaceMembers (validación mínima de datos)
    const [workspaceMembers, setWorkspaceMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [membersError, setMembersError] = useState('');

    // Estados para promoción/degradación de miembros por perfil
    const [memberIdToPromote, setMemberIdToPromote] = useState(null);
    const [memberIdToDemote, setMemberIdToDemote] = useState(null);
    const [isProcessingMemberAction, setIsProcessingMemberAction] = useState(false);
    const [memberActionError, setMemberActionError] = useState('');

   // Ref para scroll automático al añadir perfil
     const profilesContainerRef = useRef(null);
     const lastProfileRef = useRef(null);

     // Estado para perfiles expandidos/contraídos
     const [expandedProfiles, setExpandedProfiles] = useState(new Set());

     // Estado para mostrar/ocultar la lista de perfiles (inicialmente oculta)
     const [profilesVisible, setProfilesVisible] = useState(false);



    useEffect(() => {
     if (isOpen) {
       // Inicializar desde config actual: solo perfiles activos son editables
       setCompanyName(config.companyName);
       setProfiles(config.profiles.filter(isActiveProfile).map(p => ({ ...p })));
       setPendingImageDeletions(new Set());

       setColorCodeEnabled(config.colorCodeEnabled ?? true);
       setShareWhatsAppAccess(config.shareWhatsAppAccess ?? true);
       setShareFleetAlerts(config.shareFleetAlerts ?? true);
       setShowAllProfilesInfo(config.showAllProfilesInfo ?? false);
       setAllowCrossProfileClientsConsultation(config.allowCrossProfileClientsConsultation ?? false);

       // Resetear visibilidad de lista de perfiles: siempre empezar oculta
       setProfilesVisible(false);

       // Inicializar draft desde Firestore
      const count = String(config.routeStatesConfig?.mode || '4');
      setDraftCount(count);

      // Normalizar labels: siempre array de 4
      const labels = Array.isArray(config.statusLabels) 
        ? config.statusLabels 
        : [
            config.statusLabels?.go || 'Go',
            config.statusLabels?.salimos || 'Salimos',
            config.statusLabels?.volvemos || 'Volvemos',
            config.statusLabels?.fin || 'Fin',
          ];

       // Inicializar colores desde Firestore
       const colors = config.routeStatesConfig?.colors || {};
       setDraftColors({
         go: colors.go || 'green',
         salimos: colors.salimos || 'blue',
         volvemos: colors.volvemos || 'yellow',
         fin: colors.fin || 'red',
       });

      setDraftLabels(labels.slice(0, 4).concat(Array(4).fill('').slice(labels.length)));
    }
  }, [isOpen, config]);

  // Función reutilizable para refrescar la lista de miembros del workspace
  const refreshWorkspaceMembers = async () => {
    try {
      setLoadingMembers(true);
      setMembersError('');
      const result = await listWorkspaceMembersCallable();
      if (result.data && Array.isArray(result.data.members)) {
        setWorkspaceMembers(result.data.members);
      } else {
        setMembersError('Formato de respuesta inesperado.');
      }
    } catch (error) {
      console.error('[SettingsModal] Error refreshing workspace members:', error);
      setMembersError(error.message || 'No se pudo cargar la lista de cuentas.');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Cargar lista de cuentas vinculadas al workspace (Fase 5.2)
  useEffect(() => {
    if (isOpen && userRole === 'admin') {
      refreshWorkspaceMembers();
    }
  }, [isOpen, userRole]);

   const handleImageUpload = async (profileIndex, file) => {
     if (!file || !workspaceId) return;
     const profile = profiles[profileIndex];
     const profileId = profile.id || profile.name;
     setUploadingIndex(profileIndex);
     try {
       const storageRef = ref(storage, `workspaces/${workspaceId}/profileImages/${profileId}`);
       
       // Timeout de 30s para prevenir que uploadBytes se cuelgue indefinidamente
       const uploadWithTimeout = new Promise((resolve, reject) => {
         const timeoutId = setTimeout(() => {
           reject(new Error('Timeout: la subida tardó demasiado (30s). Verifica que Firebase Storage esté activado en la consola.'));
         }, 30000);
         
         uploadBytes(storageRef, file)
           .then((snapshot) => {
             clearTimeout(timeoutId);
             return getDownloadURL(snapshot.ref);
           })
           .then(resolve)
           .catch((err) => {
             clearTimeout(timeoutId);
             reject(err);
           });
       });
       
       const downloadUrl = await uploadWithTimeout;
       const updatedProfiles = [...profiles];
       updatedProfiles[profileIndex] = { ...updatedProfiles[profileIndex], imageUrl: downloadUrl };
       setProfiles(updatedProfiles);
       toast({ title: "Imagen subida", description: "Imagen actualizada correctamente." });
     } catch (error) {
       console.error("[IMAGE_UPLOAD_ERROR]", error);
       toast({ 
         title: "Error", 
         description: error.message?.includes('Timeout') ? error.message : "No se pudo subir la imagen.", 
         variant: "destructive" 
       });
     } finally {
       setUploadingIndex(null);
     }
   };

   const handleImageDelete = (profileIndex) => {
     const profile = profiles[profileIndex];
     const profileId = profile.id || profile.name;
     // Marcar para borrar al guardar. No se toca Storage todavía.
     setPendingImageDeletions(prev => new Set([...prev, profileId]));
     const updatedProfiles = [...profiles];
     updatedProfiles[profileIndex] = { ...updatedProfiles[profileIndex], imageUrl: '' };
     setProfiles(updatedProfiles);
     toast({ title: "Imagen marcada", description: "Se eliminará de Storage al guardar los cambios." });
   };

  const handleProfileNameChange = (index, newName) => {
    const updatedProfiles = [...profiles];
    updatedProfiles[index].name = newName;
    setProfiles(updatedProfiles);
  };

  const handleProfileWhatsAppChange = (index, phone) => {
    const updatedProfiles = [...profiles];
    updatedProfiles[index].whatsapp = phone;
    setProfiles(updatedProfiles);
  };
  const effectiveProfileLimit = Number.isInteger(maxProfiles)
  ? maxProfiles
  : permissions.canUseMultipleProfiles
    ? 10
    : 1;

const hasReachedProfileLimit = profiles.length >= effectiveProfileLimit;
   const addProfile = () => {
  if (hasReachedProfileLimit) {
    toast({
      title: "Límite de perfiles alcanzado",
      description: `Tu plan permite hasta ${effectiveProfileLimit} ${
        effectiveProfileLimit === 1 ? 'perfil' : 'perfiles'
      }.`,
      variant: "destructive",
    });
    return;
  }

  const newProfile = {
    id: crypto.randomUUID(),
    name: `Prfil${profiles.length + 1}`,
    whatsapp: ''
  };

  const newProfiles = [...profiles, newProfile];
  setProfiles(newProfiles);

  setProfilesVisible(true);

  setExpandedProfiles(prev => new Set([...prev, newProfile.id]));

  setTimeout(() => {
    lastProfileRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }, 100);
};

  // Función para alternar la expansión de un perfil
  const toggleProfileExpanded = (profileId) => {
    setExpandedProfiles(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) {
        next.delete(profileId);
      } else {
        next.add(profileId);
      }
      return next;
    });
  };

  // Función para expandir todos los perfiles
  const expandAllProfiles = () => {
    const allProfileIds = new Set(profiles.map(p => p.id ?? p.name));
    setExpandedProfiles(allProfileIds);
  };

  // Función para cerrar todos los perfiles
  const collapseAllProfiles = () => {
    setExpandedProfiles(new Set());
  };

  // Determinar si todos están expandidos, todos contraídos, o estado mixto
  const allProfileIds = new Set(profiles.map(p => p.id ?? p.name));
  const expandedCount = expandedProfiles.size;
  const totalCount = allProfileIds.size;
  const allExpanded = expandedCount === totalCount && totalCount > 0;
  const allCollapsed = expandedCount === 0;

  // Función para toggle general
  const toggleAllProfiles = () => {
    if (allExpanded) {
      collapseAllProfiles();
    } else {
      expandAllProfiles();
    }
  };

  // Detectar perfiles nuevos sin guardar (existentes en profiles pero no en config.profiles)
  const newProfilesWithoutSave = React.useMemo(() => {
    return profiles.filter(
      (localProfile) =>
        !config.profiles.some(
          (remoteProfile) =>
            (remoteProfile.id ?? remoteProfile.name) ===
            (localProfile.id ?? localProfile.name)
        )
    );
  }, [profiles, config.profiles]);

  // Filtrar opciones del desplegable: solo perfiles persistidos y libres
  const availableProfilesForWorker = React.useMemo(() => {
    // Solo perfiles que ya existen en config.profiles (persistidos)
    const persistedProfiles = profiles.filter((profile) =>
      config.profiles.some(
        (remoteProfile) =>
          (remoteProfile.id ?? remoteProfile.name) ===
          (profile.id ?? profile.name)
      )
    );

    // Excluir perfiles que ya estén vinculados a alguna cuenta
    return persistedProfiles.filter((profile) => {
      const profileId = profile.id ?? profile.name;
      return !workspaceMembers.some((m) => m.assignedProfileId === profileId);
    });
  }, [profiles, config.profiles, workspaceMembers]);

  const removeProfile = (index) => {
    if (profiles.length > 1) {
      const updatedProfiles = profiles.filter((_, i) => i !== index);
      setProfiles(updatedProfiles);
    } else {
      toast({
        title: "Mínimo un perfil",
        description: "Debe haber al menos un perfil.",
        variant: "destructive",
      });
    }
  };

  /**
   * Suspende un perfil activo durante 7 días mediante la callable backend
   * suspendProfile. No modifica profiles[] manualmente: la actualización real
   * llega vía el listener de config.profiles (Firestore), que reflejará el
   * nuevo status 'pending_deletion' y deletionScheduledAt calculados en backend.
   */
  const handleSuspendProfile = async () => {
    if (!profileIdToSuspend || isSuspending) return;

    setIsSuspending(true);
    setSuspendError('');
    try {
      await suspendProfileCallable({ workspaceId, profileId: profileIdToSuspend });
      setProfileIdToSuspend(null);
      setSuspendError('');
      setSuspendConfirmStep(null);
    } catch (error) {
      console.error('[SettingsModal] Error suspending profile:', error);
      setSuspendError(error.message || 'No se pudo suspender el perfil. Inténtalo de nuevo.');
      // Segundo diálogo permanece abierto: no se toca profiles[].
    } finally {
      setIsSuspending(false);
    }
  };

   /**
    * Recupera un perfil pending_deletion mediante la callable backend
    * restoreProfile. No modifica profiles[] ni config.profiles manualmente:
    * el perfil reaparecerá entre los activos vía el listener real cuando
    * Firestore refleje el nuevo status 'active'.
    * Devuelve true si la recuperación fue exitosa, false en caso de error.
    */
   const handleRestoreProfile = async (profileId) => {
     if (restoringProfileId) return false; // impide doble pulsación (una recuperación a la vez)

     setRestoringProfileId(profileId);
     setRestoreErrors((prev) => ({ ...prev, [profileId]: '' }));
     try {
       await restoreProfileCallable({ workspaceId, profileId });
       // Éxito: no se toca nada localmente, se espera el listener de config.profiles.
       return true;
     } catch (error) {
       console.error('[SettingsModal] Error restoring profile:', error);
       setRestoreErrors((prev) => ({
         ...prev,
         [profileId]: error.message || 'No se pudo recuperar el perfil. Inténtalo de nuevo.',
       }));
       // El perfil permanece en pendingProfiles: no se modifican datos locales.
       return false;
     } finally {
       setRestoringProfileId(null);
     }
   };

   /**
    * Elimina un perfil pending_deletion de forma permanente e irreversible.
    * Solo se llama tras confirmación en diálogo irreversible.
    * No modifica profiles[] ni config.profiles manualmente: se espera que
    * el backend elimine completamente el perfil y Firestore refleje el cambio.
    */
   const handleDeleteProfileNow = async () => {
     if (!profileIdToDeleteNow || isDeletingNow) return;

     setIsDeletingNow(true);
     setDeleteNowError('');
     try {
       await deleteProfileNowCallable({ workspaceId, profileId: profileIdToDeleteNow });
       // Éxito: cerrar diálogo y esperar al listener de config.profiles
       setProfileIdToDeleteNow(null);
       setDeleteNowError('');
     } catch (error) {
       console.error('[SettingsModal] Error deleting profile now:', error);
       setDeleteNowError(error.message || 'No se pudo eliminar el perfil. Inténtalo de nuevo.');
       // Mantener diálogo abierto para reintentos
     } finally {
       setIsDeletingNow(false);
     }
   };

  /**
   * Manejador para cambios en un label del draft
   */

  const handleDraftLabelChange = (index, value) => {

    const newLabels = [...draftLabels];
    newLabels[index] = value;
    setDraftLabels(newLabels);
  };

  /**
   * Crea o cambia el PIN de administrador.
   * El administrador ya está autenticado (desbloqueó la rueda amarilla para llegar aquí),
   * por lo que no se exige el PIN anterior: basta con nuevo PIN + confirmación.
   * Guarda solo adminPinHash/adminPinSalt (nunca el PIN en texto plano).
   */
  const handleSaveAdminPin = async () => {
    const formatCheck = validatePinFormat(adminNewPinInput);
    if (!formatCheck.valid) {
      toast({
        title: 'PIN inválido',
        description: formatCheck.message,
        variant: 'destructive',
      });
      return;
    }
    if (adminNewPinInput !== adminConfirmPinInput) {
      toast({
        title: 'Los PIN no coinciden',
        description: 'El PIN nuevo y su repetición deben ser iguales.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingAdminPin(true);
    try {
      const salt = await generateSalt();
      const { hash, iterations, version } = await hashPin(adminNewPinInput, salt);

      const success = await updateAdminSecurityConfig({
        adminPinHash: hash,
        adminPinSalt: salt,
        adminPinIterations: iterations,
        adminPinVersion: version,
      });

      if (success) {
        setAdminNewPinInput('');
        setAdminConfirmPinInput('');
        toast({
          title: '✅ PIN de administrador guardado',
          description: adminPinHash ? 'El PIN de administrador se ha actualizado.' : 'Se ha creado el PIN de administrador.',
          duration: 2500,
        });
      } else {
        toast({
          title: 'Error al guardar',
          description: 'No se pudo guardar el PIN de administrador.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('[SettingsModal] Error saving admin PIN:', error);
      toast({
        title: 'Error al guardar el PIN',
        description: 'No se pudo generar/guardar el PIN. Revisa la compatibilidad del navegador.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingAdminPin(false);
    }
  };

  /**
   * Elimina el PIN de administrador previa confirmación en diálogo.
   * El admin ya está autenticado/desbloqueado (llegó a esta pantalla mediante
   * el mecanismo existente), por lo que no se exige el PIN actual.
   * Tras eliminarse, AppHeader interpretará el acceso a Ajustes generales
   * como directo (mismo camino que "sin PIN configurado").
   */
  const handleRemoveAdminPin = async () => {
    setIsRemovingAdminPin(true);
    try {
      const success = await removeAdminPinConfig();
      if (success) {
        toast({
          title: '🔓 PIN de administrador eliminado',
          description: 'Ajustes generales se abrirá sin pedir PIN.',
          duration: 2500,
        });
      } else {
        toast({
          title: 'Error al quitar el PIN',
          description: 'No se pudo eliminar el PIN de administrador. Inténtalo de nuevo.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('[SettingsModal] Error removing admin PIN:', error);
      toast({
        title: 'Error al quitar el PIN',
        description: 'No se pudo eliminar el PIN de administrador. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsRemovingAdminPin(false);
      setShowRemoveAdminPinConfirm(false);
    }
  };

   /**
    * El administrador restablece (elimina) el PIN de un perfil sin conocer el PIN actual.

    * Se opta por "eliminar la protección" en lugar de asignar un PIN temporal: es la opción
    * más simple y seguro-por-defecto (el usuario del perfil puede crear uno nuevo cuando quiera,
    * y no queda un PIN temporal circulando que alguien deba recordar/transmitir).
    * 
    * Ahora recibe profileId en lugar de índice para ser más estable.
    */
   const handleResetProfilePin = async (profileId) => {
     // Buscar el perfil por profileId
     const profileIndex = profiles.findIndex((p) => (p.id ?? p.name) === profileId);
     if (profileIndex === -1) return;

     const profile = profiles[profileIndex];

     try {
       if (workspaceId) {
         const revoked = await revokeTrustedDevices(workspaceId, db, profileId);
         if (!revoked) {
           throw new Error('No se pudieron revocar los dispositivos confiables.');
         }
         removeLocalDeviceToken(workspaceId, profileId);
       }

       const updatedProfiles = profiles.map((item, itemIndex) => {
         if (itemIndex !== profileIndex) return item;

         const {
           pinHash,
           pinSalt,
           pinIterations,
           pinVersion,
           pinUpdatedAt,
           ...profileWithoutPin
         } = item;

         return profileWithoutPin;
       });

       await updateProfilesConfig(updatedProfiles);
       setProfiles(updatedProfiles);
       unlockProfile(profileId);

       toast({
         title: '🔓 PIN eliminado',
         description: `Se ha quitado la protección por PIN de "${profile.name}".`,
         duration: 2500,
       });
     } catch (error) {
       console.error('[SettingsModal] Error removing profile PIN:', error);
       toast({
         title: 'Error al quitar el PIN',
         description: 'No se pudo quitar el PIN de forma segura. Inténtalo de nuevo.',
         variant: 'destructive',
       });
     }
   };

   /**
    * Vincula un trabajador ya registrado y verificado con el administrador actual
    * asignándole exactamente un perfil mediante la callable inviteWorkerWithProfile.
    */
   const handleInviteWorker = async (e) => {
     e.preventDefault();
     setInviteError('');
     setInviteSuccess('');

     const trimmedEmail = inviteEmail.trim().toLowerCase();
     if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
       setInviteError('Por favor, ingresa un email válido.');
       return;
     }

     if (!selectedProfileId) {
       setInviteError('Por favor, selecciona un perfil.');
       return;
     }

     setInviteLoading(true);
     try {
       const result = await inviteWorkerCallable({
         workerEmail: trimmedEmail,
         profileId: selectedProfileId,
       });

       if (result.data && result.data.success) {
         setInviteSuccess(`✅ Trabajador vinculado exitosamente (ID: ${result.data.workerUid})`);
         setInviteEmail('');
         setSelectedProfileId('');
       } else {
         setInviteError('Error: La respuesta del servidor no fue exitosa.');
       }
     } catch (error) {
       console.error('[SettingsModal] Error inviting worker:', error);
       const errorMsg = error.message || 'No se pudo vincular el trabajador. Inténtalo de nuevo.';
       setInviteError(errorMsg);
     } finally {
       setInviteLoading(false);
     }
   };

   /**
    * Promociona a Admin un miembro User específico por uid
    */
   const handlePromoteProfileMember = async (memberId) => {
     if (!memberId || isProcessingMemberAction) return;

     setIsProcessingMemberAction(true);
     setMemberActionError('');
     try {
       const result = await promoteUserToAdminCallable({ targetUid: memberId });
       if (result.data && result.data.success) {
         toast({
           title: '✅ Promoción exitosa',
           description: 'La cuenta ahora es Admin.',
           duration: 2500,
         });
         await refreshWorkspaceMembers();
         setMemberIdToPromote(null);
       } else {
         setMemberActionError('Error: La respuesta del servidor no fue exitosa.');
       }
     } catch (error) {
       console.error('[SettingsModal] Error promoting member:', error);
       setMemberActionError(error.message || 'No se pudo promocionar la cuenta. Inténtalo de nuevo.');
     } finally {
       setIsProcessingMemberAction(false);
     }
   };

   /**
    * Degrada de Admin a User un miembro Admin secundario específico por uid
    */
   const handleDemoteAdminMember = async (memberId) => {
     if (!memberId || isProcessingMemberAction) return;

     setIsProcessingMemberAction(true);
     setMemberActionError('');
     try {
       const result = await demoteAdminToUserCallable({ targetUid: memberId });
       if (result.data && result.data.success) {
         toast({
           title: '✅ Degradación exitosa',
           description: 'La cuenta ahora es User.',
           duration: 2500,
         });
         await refreshWorkspaceMembers();
         setMemberIdToDemote(null);
       } else {
         setMemberActionError('Error: La respuesta del servidor no fue exitosa.');
       }
     } catch (error) {
       console.error('[SettingsModal] Error demoting member:', error);
       setMemberActionError(error.message || 'No se pudo degradar la cuenta. Inténtalo de nuevo.');
     } finally {
       setIsProcessingMemberAction(false);
     }
   };



  /**
   * Guardar cambios en Firestore
   */
  const handleSave = async () => {
    const trimmedName = companyName.trim();

    const profileNames = profiles.map(p => p.name.trim());
    if (profileNames.some(name => name === '')) {
      toast({
        title: "Error de validación",
        description: "El nombre de los perfiles no puede estar vacío.",
        variant: "destructive",
      });
      return;
    }
    if (new Set(profileNames).size !== profileNames.length) {
      toast({
        title: "Error de validación",
        description: "Los nombres de los perfiles deben ser únicos.",
        variant: "destructive",
      });
      return;
    }

    // Capturar, ANTES de guardar, los profileId canónicos (id ?? name) de los
    // perfiles que son realmente nuevos (existen en profiles[] local pero no
    // en config.profiles remoto). Se usa newProfilesWithoutSave, ya calculado
    // por comparación de id (o name como fallback si no hay id), nunca por
    // nombre cuando existe id. Este snapshot no cambia aunque el guardado
    // modifique profiles/config más adelante.
    const profilesToSeedAlertsFor = newProfilesWithoutSave.map((p) => ({
      profileKey: p.id ?? p.name,
      profileId: p.id ?? p.name,
    }));

    // Fusionar perfiles activos editados con los no activos más recientes de
    // config.profiles (fuente de verdad remota). Así, si un perfil fue
    // suspendido (pending_deletion/deleting) mientras el modal estaba abierto
    // con un snapshot local antiguo, "Guardar Cambios" no lo revierte a
    // 'active' ni pierde su deletionScheduledAt: se preserva tal cual está
    // en config.profiles, identificado por id ?? name.
    const mergedProfiles = config.profiles

      .map((profile) => profile.id ?? profile.name)
      .filter((profileId, index, allIds) => allIds.indexOf(profileId) === index)
      .map((profileId) => {
        const remoteProfile = config.profiles.find(
          (profile) => (profile.id ?? profile.name) === profileId
        );

        // No activo (suspendido/deleting): se preserva tal cual, status y
        // deletionScheduledAt intactos, sin importar el snapshot local.
        if (!isActiveProfile(remoteProfile)) {
          return remoteProfile;
        }

        // Activo: prevalece la edición local si existe; si el perfil fue
        // recuperado recientemente (activo en config.profiles pero ausente
        // del snapshot local por haberse abierto el modal antes), se usa el
        // remoto para que no desaparezca de mergedProfiles.
        const localProfile = profiles.find(
          (profile) => (profile.id ?? profile.name) === profileId
        );

        return localProfile ?? remoteProfile;
      })
      // Perfiles activos nuevos añadidos en el modal (no existían en config.profiles)
      .concat(
        profiles.filter(
          (localProfile) =>
            !config.profiles.some(
              (remoteProfile) =>
                (remoteProfile.id ?? remoteProfile.name) ===
                (localProfile.id ?? localProfile.name)
            )
        )
      );


    // Actualizar config general (empresa, perfiles, etc.)
    const routeStatesConfig = {
      mode: draftCount,
      labelsFor2States: {
        state1: draftLabels[0],
        state2: draftLabels[1],
      },
      labelsFor3States: {
        state1: draftLabels[0],
        state2: draftLabels[1],
        state3: draftLabels[2],
      },
      colors: draftColors,
    };

    updateConfig({ 
      companyName: trimmedName, 
      profiles: mergedProfiles,
      colorCodeEnabled,

      shareWhatsAppAccess,
      shareFleetAlerts,
      statusLabels: {
        go: draftLabels[0],
        salimos: draftLabels[1],
        volvemos: draftLabels[2],
        fin: draftLabels[3],
      },
      routeStatesConfig,
    });

    // Ejecutar borrado real de Storage para imágenes marcadas
    if (pendingImageDeletions.size > 0 && workspaceId) {
      for (const profileId of pendingImageDeletions) {
        try {
          const storageRef = ref(storage, `workspaces/${workspaceId}/profileImages/${profileId}`);
          await deleteObject(storageRef);
        } catch (error) {
          console.warn("[IMAGE_DELETE] Archivo no encontrado en Storage, continuando:", error.message);
        }
      }
      setPendingImageDeletions(new Set());
    }

    // Guardar Estados de Ruta en Firestore (sincronizado)
    await updateRouteStatusConfig({
      count: parseInt(draftCount),
      labels: draftLabels,
      enabled: colorCodeEnabled,
      shareWhatsAppAccess: shareWhatsAppAccess,
       colors: draftColors,

      shareFleetAlerts: shareFleetAlerts,
    });

    // Guardar Profiles en Firestore (sincronizado): usar la fusión, no el
    // snapshot local sin combinar, para no revertir suspensiones recientes.
    const profilesSaveSuccess = await updateProfilesConfig(mergedProfiles);

    // Sembrar el dismiss del aviso broadcast actual SOLO para los perfiles
    // que eran nuevos antes de este guardado, y SOLO si el guardado de
    // perfiles terminó correctamente. Si updateProfilesConfig devolvió false
    // (fallo), no se siembra ningún dismiss: los perfiles nuevos no quedan
    // persistidos y no debe crearse un dismiss "huérfano" para ellos.
    // No afecta a perfiles existentes, ACK/reply ni dismiss manual.
    if (profilesSaveSuccess && workspaceId && profilesToSeedAlertsFor.length > 0) {
      for (const { profileKey, profileId } of profilesToSeedAlertsFor) {
        try {
          await seedDismissedForNewProfile(workspaceId, profileKey, profileId);
        } catch (error) {
          // No bloquear el guardado ya realizado por un fallo al sembrar el
          // dismiss del aviso: se registra y se continúa.
          console.error('[SettingsModal] Error sembrando dismiss de aviso para perfil nuevo:', profileKey, error);
        }
      }
    }

    // Guardar Metadatos del Workspace en Firestore (para administración)
    await updateWorkspaceMeta({ companyName: trimmedName, profiles: mergedProfiles, showAllProfilesInfo, allowCrossProfileClientsConsultation });


    toast({

      title: "¡Guardado!",
      description: "La configuración se ha actualizado correctamente.",
      duration: 2000,
    });
    onClose();
  };

  const handleCancel = () => {
    // Solo cerrar, sin guardar cambios del draft
    onClose();
  };

  const handleLogout = async () => {
    await authLogout();
    onClose();
  };

  if (userRole === 'worker') {
    return null;
  }

  return (
      <Dialog open={isOpen} onOpenChange={onClose}>
       <DialogContent
        className="max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden px-3 py-6 sm:p-6"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
         <DialogHeader>
           <DialogTitle className="text-2xl">Configuración</DialogTitle>
         </DialogHeader>

         {/* Diálogo irreversible: eliminar perfil pending_deletion ahora */}
         <AlertDialog open={profileIdToDeleteNow !== null} onOpenChange={(isOpen) => {
           if (!isOpen) {
             setProfileIdToDeleteNow(null);
             setDeleteNowError('');
           }
         }}>
           <AlertDialogContent>
             <AlertDialogHeader>
               <AlertDialogTitle>¿Eliminar definitivamente este perfil?</AlertDialogTitle>
               <AlertDialogDescription>
                 Esta acción no se puede deshacer. El perfil y todos sus datos asociados se eliminarán ahora de forma permanente.
               </AlertDialogDescription>
             </AlertDialogHeader>
             {deleteNowError && (
               <p className="text-sm text-destructive px-6">{deleteNowError}</p>
             )}
             <AlertDialogFooter>
               <AlertDialogCancel disabled={isDeletingNow}>Cancelar</AlertDialogCancel>
               <AlertDialogAction
                 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                 disabled={isDeletingNow}
                 onClick={handleDeleteProfileNow}
               >
                 {isDeletingNow ? 'Eliminando…' : 'Eliminar definitivamente'}
               </AlertDialogAction>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>

        {/* Diálogo de confirmación para recuperar perfil pending_deletion */}
        <AlertDialog open={profileIdToRestore !== null} onOpenChange={(isOpen) => {
          if (!isOpen && !restoringProfileId) {
            setProfileIdToRestore(null);
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Recuperar este perfil?</AlertDialogTitle>
              <AlertDialogDescription>
                El perfil volverá a estar activo y aparecerá de nuevo en el selector y en la aplicación.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {restoreErrors[profileIdToRestore] && (
              <p className="text-sm text-destructive px-6">{restoreErrors[profileIdToRestore]}</p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={restoringProfileId === profileIdToRestore}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                disabled={restoringProfileId === profileIdToRestore}
                onClick={async (e) => {
                  e.preventDefault();
                  const profileId = profileIdToRestore;
                  const restored = await handleRestoreProfile(profileId);
                  if (restored === true) {
                    setProfileIdToRestore(null);
                  }
                }}
              >
                {restoringProfileId === profileIdToRestore ? 'Recuperando…' : 'Recuperar perfil'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="space-y-6 py-4 w-[calc(100%_-_24px)] mx-auto sm:w-auto sm:mx-0 min-w-0">
           {/* Datos de Empresa */}
           <div className="space-y-4 p-4 border rounded-lg bg-card">
             <h3 className="text-lg font-semibold text-foreground dark:text-white">Datos de Empresa</h3>
             
             <div>
               <Label htmlFor="companyName" className="text-base">Nombre de la Empresa</Label>
               {!permissions.canUseCompanyName && (
                 <p className="text-xs text-muted-foreground mt-1">Disponible en planes Premium</p>
               )}
               <Input
                 id="companyName"
                 value={companyName}
                 onChange={(e) => setCompanyName(e.target.value)}
                 maxLength={15}
                 className="mt-2"
                 placeholder="Escribe el nombre de tu empresa"
                 disabled={!permissions.canUseCompanyName}
               />
             </div>
           </div>

            {/* Edición de perfiles */}
            <div className="space-y-4 p-4 border rounded-lg bg-card">
              <h3 className="text-lg font-semibold text-foreground dark:text-white">Edición de perfiles</h3>
              
              {/* Perfiles (Vehículos) */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h4 className="text-base font-semibold">Perfiles</h4>
                   <Button 
                     onClick={addProfile} 
                     size="sm"
                     disabled={hasReachedProfileLimit}
                   >
                    <Plus className="mr-2 h-4 w-4" /> Añadir
                  </Button>
                </div>
               <p className="text-xs text-muted-foreground -mt-2">
                 Añade nuevos perfiles o elimina los que no necesites.
               </p>

               {/* Botón Mostrar/Ocultar lista de perfiles */}
               {profiles.length > 0 && (
                 <div className="space-y-2">
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => setProfilesVisible(!profilesVisible)}
                     className="w-full"
                   >
                     {profilesVisible ? 'Ocultar perfiles' : 'Mostrar perfiles'}
                   </Button>

                   {/* Botón Desplegar/Plegar todos (solo visible cuando lista está abierta) */}
                   {profilesVisible && (
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={toggleAllProfiles}
                       className="w-full"
                     >
                       {allExpanded ? 'Plegar perfiles' : 'Desplegar perfiles'}
                     </Button>
                   )}
                 </div>
               )}

               {profilesVisible && (
               <div className="space-y-4">
                 {profiles.map((profile, index) => {
                   const profileId = profile.id ?? profile.name;
                   const hasPin = Boolean(profile.pinHash);

                    const isNewProfileWithoutSave = newProfilesWithoutSave.some(
                      (p) => (p.id ?? p.name) === (profile.id ?? profile.name)
                    );

                    return (
                      <div key={index} className="border rounded-md p-3 bg-muted/30 space-y-3" ref={index === profiles.length - 1 ? lastProfileRef : null}>
                         <div className="flex items-center gap-2">
                           <Label className="text-sm font-medium flex-shrink-0">
                             {`Perfil ${index + 1}`}
                           </Label>
                           {isNewProfileWithoutSave && (
                             <span className="text-xs font-semibold bg-amber-500 text-white px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                               SIN GUARDAR
                             </span>
                           )}
                           <Input
                             value={profile.name}
                             onChange={(e) => handleProfileNameChange(index, e.target.value)}
                             maxLength={7}
                             placeholder="Nombre del perfil"
                             className="flex-1 min-w-0 max-w-xs"
                             disabled={Boolean(profile.pinHash)}
                           />

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleProfileExpanded(profileId)}
                            className="h-8 w-8 flex-shrink-0"
                          >
                            <svg
                              className={`h-4 w-4 transition-transform ${expandedProfiles.has(profileId) ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                          </Button>

                        </div>

                       {/* Contenido expandible: PIN, aviso sin guardar, cuenta vinculada */}
                       {expandedProfiles.has(profileId) && (
                         <>
                           {/* Botón de papelera para eliminar/suspender el perfil */}
                           <Button
                             variant="ghost"
                             size="sm"
                             onClick={() => {
                               setProfileIdToSuspend(profile.id ?? profile.name);
                               setSuspendError('');
                               setSuspendConfirmStep('first');
                             }}
                             disabled={profiles.length <= 1}
                             className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full justify-start"
                           >
                             <Trash2 className="h-4 w-4 mr-2" />
                             Eliminar perfil
                           </Button>

                           {/* Estado y acción de PIN (si los permisos lo permiten) */}
                           {!permissions.canUseRouteStates ? null : (
                             <div className="flex items-center justify-between gap-3 rounded-md border border-dashed p-2 bg-muted/50">
                               <p className="text-xs text-muted-foreground">
                                 {hasPin ? 'CON PIN' : 'SIN PIN'}
                               </p>
                               {hasPin ? (
                                 <Button
                                   type="button"
                                   variant="outline"
                                   size="sm"
                                   className="text-xs h-7 px-2"
                                   onClick={() => setProfileIdToResetPin(profileId)}
                                 >
                                   Quitar PIN
                                 </Button>
                               ) : (
                                 <Button
                                   type="button"
                                   variant="outline"
                                   size="sm"
                                   className="text-xs h-7 px-2"
                                   onClick={() => onConfigureProfilePin?.(profileId)}
                                 >
                                   Editar Perfil
                                 </Button>
                               )}
                             </div>
                           )}

                           {/* Aviso de perfil sin guardar */}
                           {isNewProfileWithoutSave && (
                             <p className="text-xs text-muted-foreground italic">
                               Guarda los cambios para crear este perfil.
                             </p>
                           )}

                           {/* Información de cuenta vinculada y gestión de rol */}
                           {(() => {
                             const member = workspaceMembers.find((m) => m.assignedProfileId === profileId);
                             if (!member) return null;

                             const memberIdentity = member.email || member.displayName || 'Cuenta vinculada';
                             const isProcessing = memberIdToPromote === member.uid || memberIdToDemote === member.uid;

                              return (
                                <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2 sm:gap-3 rounded-md border p-2 bg-muted/50">
                                  <div className="flex items-center gap-2 flex-grow min-w-0">
                                   <p className="text-xs text-muted-foreground truncate">{memberIdentity}</p>
                                   {member.role === 'admin' && member.isPrimaryAdmin && (
                                     <span className="text-xs font-semibold bg-yellow-100 dark:bg-yellow-900 text-yellow-900 dark:text-yellow-100 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                                       ADMIN PPAL
                                     </span>
                                   )}
                                   {member.role === 'admin' && !member.isPrimaryAdmin && (
                                     <span className="text-xs font-semibold bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                                       ADMIN
                                     </span>
                                   )}
                                   {member.role === 'worker' && (
                                     <span className="text-xs font-semibold bg-emerald-600 text-white px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                                       USER
                                     </span>
                                   )}
                                 </div>

                                 {member.role === 'worker' && (
                                   <Button
                                     type="button"
                                     variant="outline"
                                     size="sm"
                                     className="text-xs h-7 px-2 whitespace-nowrap flex-shrink-0"
                                     disabled={isProcessing}
                                     onClick={() => {
                                       setMemberActionError('');
                                       setMemberIdToPromote(member.uid);
                                     }}
                                   >
                                     {memberIdToPromote === member.uid && isProcessingMemberAction ? 'Promoviendo…' : 'Hacer Admin'}
                                   </Button>
                                 )}

                                 {member.role === 'admin' && !member.isPrimaryAdmin && (
                                   <Button
                                     type="button"
                                     variant="outline"
                                     size="sm"
                                     className="text-xs h-7 px-2 text-destructive hover:text-destructive whitespace-nowrap flex-shrink-0"
                                     disabled={isProcessing}
                                     onClick={() => {
                                       setMemberActionError('');
                                       setMemberIdToDemote(member.uid);
                                     }}
                                   >
                                     {memberIdToDemote === member.uid && isProcessingMemberAction ? 'Degradando…' : 'Quitar Admin'}
                                   </Button>
                                 )}
                               </div>
                             );
                           })()}
                         </>
                       )}
                     </div>
                   );
                 })}
               </div>
               )}
              </div>

              <div className="pt-3 border-t" />

              {/* Vincular cuenta - integrado en Edición de perfiles */}
              {userRole === 'admin' && (
                <div className="space-y-3">
                  <div>
                    <h4 className="text-base font-semibold">Vincular cuenta</h4>
                    <p className="text-sm text-muted-foreground">
                      La cuenta debe haberse registrado y verificado su correo antes de vincularla.
                    </p>
                  </div>

                  {profiles.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                      Crea al menos un perfil antes de vincular cuentas.
                    </p>
                  ) : (
                    <form onSubmit={handleInviteWorker} className="space-y-4">
                      <div>
                        <Label htmlFor="worker-email" className="text-sm">
                          Email de la cuenta
                        </Label>
                        <Input
                          id="worker-email"
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="cuenta@ejemplo.com"
                          required
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="worker-profile" className="text-sm">
                          Perfil a asignar
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1 mb-2">
                          El perfil debe estar creado previamente para poder asignarlo a una cuenta.
                        </p>
                        <select
                          id="worker-profile"
                          value={selectedProfileId}
                          onChange={(e) => setSelectedProfileId(e.target.value)}
                          required
                          className="mt-1 w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                        >
                          <option value="">-- Selecciona un perfil --</option>
                          {availableProfilesForWorker.map((profile) => {
                            const profileId = String(profile.id ?? profile.name);
                            return (
                              <option key={profileId} value={profileId}>
                                {profile.name}
                              </option>
                            );
                          })}
                        </select>
                        {newProfilesWithoutSave.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Guarda los cambios para poder vincular los perfiles nuevos.
                          </p>
                        )}
                      </div>

                      {inviteError && (
                        <p className="text-sm text-destructive flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {inviteError}
                        </p>
                      )}

                      {inviteSuccess && (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {inviteSuccess}
                        </p>
                      )}

                      <Button
                        type="submit"
                        disabled={inviteLoading || !inviteEmail || !selectedProfileId}
                        className="w-full"
                      >
                        {inviteLoading ? 'Vinculando…' : 'Vincular cuenta'}
                      </Button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Perfiles pendientes de eliminación (suspendidos, recuperables durante 7 días) */}
            {pendingProfiles.length > 0 && (
              <div className="space-y-4 p-4 border rounded-lg bg-card">
                <h3 className="text-lg font-semibold text-foreground dark:text-white">
                  Perfiles pendientes de eliminación
                </h3>
                 <div className="space-y-2">
                   {pendingProfiles.map((profile) => {
                     const profileId = profile.id ?? profile.name;
                     const isDeleting = profile.status === 'deleting';
                     const isRestoringThisRow = restoringProfileId === profileId;
                     const rowError = restoreErrors[profileId];

                     return (
                       <div
                         key={profileId}
                         className="rounded-md border p-3 space-y-1"
                       >
                         <div className="flex flex-wrap items-center justify-between gap-3">
                           <div className="min-w-0 flex-grow">
                             <p className="text-sm font-medium">{profile.name}</p>
                             <p className="text-xs text-muted-foreground">
                               {isDeleting
                                 ? 'Eliminación en curso'
                                 : `Se eliminará el ${formatDeletionDate(profile.deletionScheduledAt)}`}
                             </p>
                           </div>

                            <div className="flex gap-2 flex-shrink-0">
                              {!isDeleting && (
                                <>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isRestoringThisRow || isDeletingNow}
                                    onClick={() => setProfileIdToRestore(profileId)}
                                  >
                                    {isRestoringThisRow ? 'Recuperando…' : 'Recuperar'}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    disabled={isDeletingNow}
                                    onClick={() => {
                                      setProfileIdToDeleteNow(profileId);
                                      setDeleteNowError('');
                                    }}
                                  >
                                    Eliminar ahora
                                  </Button>
                                </>
                              )}
                              {isDeleting && (
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  disabled={isDeletingNow}
                                  onClick={() => {
                                    setProfileIdToDeleteNow(profileId);
                                    setDeleteNowError('');
                                  }}
                                >
                                  {isDeletingNow && profileIdToDeleteNow === profileId ? 'Reintentando…' : 'Reintentar eliminación'}
                                </Button>
                              )}
                            </div>
                         </div>
                         {rowError && (
                           <p className="text-sm text-destructive">{rowError}</p>
                         )}
                       </div>
                     );
                   })}
                 </div>
              </div>
            )}
            {/* Perfiles bloqueados por plan */}
             {lockedByPlanProfiles.length > 0 && (
  <details className="rounded-lg border bg-card">
    <summary className="cursor-pointer p-4 font-semibold">
      Perfiles bloqueados por tu plan ({lockedByPlanProfiles.length})
    </summary>

    <div className="space-y-2 px-4 pb-4">
      {lockedByPlanProfiles.map((profile) => {
        const profileId = profile.id ?? profile.name;

        return (
          <div
            key={profileId}
            className="rounded-md border p-3"
          >
            <p className="font-medium">{profile.name}</p>
            <p className="text-sm text-muted-foreground">
              Bloqueado por tu plan actual
            </p>
          </div>
        );
      })}
    </div>
  </details>
)}



             {/* Visibilidad entre perfiles */}

             <div className="space-y-4 p-4 border rounded-lg bg-card">
               <h3 className="text-lg font-semibold text-foreground dark:text-white">Visibilidad entre perfiles</h3>
               
               {!permissions.canUseRouteStates ? (
                 <p className="text-xs text-muted-foreground">Disponible en planes Premium</p>
               ) : (
               <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="showAllProfilesInfo" className="text-base font-medium">
  Hacer visibles todos los perfiles y sus estados
</Label>
<p className="text-sm text-muted-foreground">
  Permite que los usuarios vean los demás perfiles y los colores de sus estados. Esta opción no permite consultar los clientes de otros perfiles.
</p>
                    </div>
                    <Switch
                      id="showAllProfilesInfo"
                      checked={showAllProfilesInfo}
                      onCheckedChange={setShowAllProfilesInfo}
                    />
                  </div>
                  <div className="pt-3 border-t" />
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="allowCrossProfileClientsConsultation" className="text-base font-medium">
                        Permitir consultar los clientes de otros perfiles
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Los usuarios podrán abrir otros perfiles y ver sus clientes, pero no realizar cambios ni utilizar sus acciones.
                      </p>
                    </div>
                    <Switch
                      id="allowCrossProfileClientsConsultation"
                      checked={allowCrossProfileClientsConsultation}
                      onCheckedChange={setAllowCrossProfileClientsConsultation}
                    />
                  </div>
                </>
                )}
              </div>

             {/* Comunicación entre perfiles */}

             <div className="space-y-4 p-4 border rounded-lg bg-card">
               <h3 className="text-lg font-semibold text-foreground dark:text-white">Comunicación entre perfiles</h3>
              
              {!permissions.canUseRouteStates ? (
                <p className="text-xs text-muted-foreground">Disponible en planes Premium</p>
              ) : (
              <>
               <div className="flex items-center justify-between">
                 <div className="space-y-1">
                   <Label htmlFor="shareWhatsAppAccess" className="text-base font-medium">
                     Compartir acceso directo a WhatsApp
                   </Label>
                    <p className="text-sm text-muted-foreground">
                      Muestra el acceso rápido al WhatsApp de los otros perfiles
                    </p>
                 </div>
                 <Switch
                   id="shareWhatsAppAccess"
                   checked={shareWhatsAppAccess}
                   onCheckedChange={setShareWhatsAppAccess}
                 />
               </div>

               <div className="pt-3 border-t" />

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="shareFleetAlerts" className="text-base font-medium">
                      Compartir avisos de flota
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Muestra la campana de avisos rápidos entre perfiles
                    </p>
                  </div>
                  <Switch
                    id="shareFleetAlerts"
                    checked={shareFleetAlerts}
                    onCheckedChange={setShareFleetAlerts}
                  />
                </div>

               <div className="flex items-center justify-between">
                 <div className="space-y-1">
                   <Label htmlFor="colorCodeEnabled" className="text-base font-medium">
                     Activar botones de estado entre perfiles
                   </Label>
                   <p className="text-sm text-muted-foreground">
                     Muestra los estados y colorea los perfiles según su estado actual
                   </p>
                 </div>
                 <Switch
                   id="colorCodeEnabled"
                   checked={colorCodeEnabled}
                   onCheckedChange={setColorCodeEnabled}
                 />
               </div>

              {colorCodeEnabled && (
               <div className="mt-4 space-y-4 pt-3 border-t">
                 <div>
                   <Label className="text-sm font-medium mb-3 block">Cantidad de Estados de Ruta</Label>
                   <div className="flex gap-4 flex-wrap">
                     <label className="flex items-center gap-2 cursor-pointer">
                       <input
                         type="radio"
                         name="draftCount"
                         value="2"
                         checked={draftCount === '2'}
                         onChange={(e) => setDraftCount(e.target.value)}
                         className="w-4 h-4"
                       />
                       <span className="text-sm">2 Estados</span>
                     </label>
                     <label className="flex items-center gap-2 cursor-pointer">
                       <input
                         type="radio"
                         name="draftCount"
                         value="3"
                         checked={draftCount === '3'}
                         onChange={(e) => setDraftCount(e.target.value)}
                         className="w-4 h-4"
                       />
                       <span className="text-sm">3 Estados</span>
                     </label>
                     <label className="flex items-center gap-2 cursor-pointer">
                       <input
                         type="radio"
                         name="draftCount"
                         value="4"
                         checked={draftCount === '4'}
                         onChange={(e) => setDraftCount(e.target.value)}
                         className="w-4 h-4"
                       />
                       <span className="text-sm">4 Estados</span>
                     </label>
                   </div>
                 </div>

                  {/* Función helper para mapear índice a clave de estado según modo */}
                  {(() => {
                    const getStateKeyForIndex = (index) => {
                      if (draftCount === '2') {
                        return index === 0 ? 'go' : 'fin';
                      } else if (draftCount === '3') {
                        if (index === 0) return 'go';
                        if (index === 1) return 'salimos';
                        return 'fin';
                      } else { // modo 4
                        const keys = ['go', 'salimos', 'volvemos', 'fin'];
                        return keys[index];
                      }
                    };
                    
                    const colorOptions = [
                      { label: 'Verde', value: 'green' },
                      { label: 'Azul', value: 'blue' },
                      { label: 'Amarillo', value: 'yellow' },
                      { label: 'Rojo', value: 'red' },
                    ];

                    return (


                 
                   
                      <div className="flex flex-col gap-1">
                        {draftCount >= '1' && (
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label htmlFor="label0" className="text-sm">Estado 1</Label>
                              <Input
                                id="label0"
                                value={draftLabels[0] || ''}
                                onChange={(e) => handleDraftLabelChange(0, e.target.value)}
                                placeholder="Go"
                                className="mt-1"
                              />
                            </div>
                            <select
                              value={draftColors[getStateKeyForIndex(0)]}
                              onChange={(e) => setDraftColors({...draftColors, [getStateKeyForIndex(0)]: e.target.value})}
                              className="px-2 py-1.5 bg-card border rounded text-sm text-foreground"
                            >
                              {colorOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                          </div>
                        )}
                        {draftCount >= '2' && (
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label htmlFor="label1" className="text-sm">Estado 2</Label>
                              <Input
                                id="label1"
                                value={draftLabels[1] || ''}
                                onChange={(e) => handleDraftLabelChange(1, e.target.value)}
                                placeholder="Salimos"
                                className="mt-1"
                              />
                            </div>
                            <select
                              value={draftColors[getStateKeyForIndex(1)]}
                              onChange={(e) => setDraftColors({...draftColors, [getStateKeyForIndex(1)]: e.target.value})}
                              className="px-2 py-1.5 bg-card border rounded text-sm text-foreground"
                            >
                              {colorOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                          </div>
                        )}
                        {draftCount >= '3' && (
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label htmlFor="label2" className="text-sm">Estado 3</Label>
                              <Input
                                id="label2"
                                value={draftLabels[2] || ''}
                                onChange={(e) => handleDraftLabelChange(2, e.target.value)}
                                placeholder="Volvemos"
                                className="mt-1"
                              />
                            </div>
                            <select
                              value={draftColors[getStateKeyForIndex(2)]}
                              onChange={(e) => setDraftColors({...draftColors, [getStateKeyForIndex(2)]: e.target.value})}
                              className="px-2 py-1.5 bg-card border rounded text-sm text-foreground"
                            >
                              {colorOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                          </div>
                        )}
                        {draftCount >= '4' && (
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label htmlFor="label3" className="text-sm">Estado 4</Label>
                              <Input
                                id="label3"
                                value={draftLabels[3] || ''}
                                onChange={(e) => handleDraftLabelChange(3, e.target.value)}
                                placeholder="Fin"
                                className="mt-1"
                              />
                            </div>
                            <select
                              value={draftColors[getStateKeyForIndex(3)]}
                              onChange={(e) => setDraftColors({...draftColors, [getStateKeyForIndex(3)]: e.target.value})}
                              className="px-2 py-1.5 bg-card border rounded text-sm text-foreground"
                            >
                              {colorOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  </div>
                
              )}
              </>
              )}
            </div>


            {/* Seguridad de Administrador */}
            <div className="space-y-4 p-4 border rounded-lg bg-card">
             <div className="flex items-center gap-2">
               <KeyRound className="h-5 w-5 text-muted-foreground" />
               <h3 className="text-lg font-semibold text-foreground dark:text-white">
                 {adminPinHash ? 'Cambiar PIN de administrador' : 'Crear PIN de administrador'}
               </h3>
             </div>
             <p className="text-xs text-muted-foreground">
               Protege el acceso a esta pantalla de Configuración general (rueda amarilla). Ya has verificado tu identidad para llegar aquí, así que no se pide el PIN anterior.
             </p>
             <div>
               <Label htmlFor="admin-new-pin" className="text-sm">PIN nuevo (4 a 6 dígitos)</Label>
               <Input
                 id="admin-new-pin"
                 type="password"
                 value={adminNewPinInput}
                 onChange={(e) => setAdminNewPinInput(e.target.value)}
                 placeholder="0000"
                 maxLength="6"
                 className="mt-1"
               />
             </div>
             <div>
               <Label htmlFor="admin-confirm-pin" className="text-sm">Repetir PIN nuevo</Label>
               <Input
                 id="admin-confirm-pin"
                 type="password"
                 value={adminConfirmPinInput}
                 onChange={(e) => setAdminConfirmPinInput(e.target.value)}
                 placeholder="0000"
                 maxLength="6"
                 className="mt-1"
               />
             </div>
             <Button
               type="button"
               variant="outline"
               onClick={handleSaveAdminPin}
               disabled={isSavingAdminPin}
             >
               {isSavingAdminPin ? 'Guardando...' : adminPinHash ? 'Cambiar PIN de administrador' : 'Crear PIN de administrador'}
             </Button>
             {adminPinHash && (
               <Button
                 type="button"
                 variant="ghost"
                 className="text-destructive hover:text-destructive hover:bg-destructive/10"
                 onClick={() => setShowRemoveAdminPinConfirm(true)}
                 disabled={isRemovingAdminPin}
               >
                 Quitar PIN
               </Button>
             )}
           </div>



           {/* Suscripción */}
          <div className="space-y-3 p-4 border rounded-lg bg-card">
            <h3 className="text-lg font-semibold text-foreground dark:text-white">Suscripción</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Plan actual</p>
                <p className="font-semibold">
                  {isFree ? 'Free' : isPremiumPersonal ? 'Premium Personal' : 'Premium Pro'}
                </p>
              </div>
              <Button
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                onClick={() => setIsPlanModalOpen(true)}
              >
                Ver planes y precios
              </Button>
            </div>
          </div>

           {/* Cerrar Sesión */}
           <div className="p-4 border rounded-lg bg-destructive/5">
            <h3 className="text-lg font-semibold text-foreground dark:text-white mb-3">Sesión</h3>
            <Button 
              onClick={() => setShowLogoutConfirm(true)}
              variant="destructive"
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" /> Cerrar Sesión
            </Button>
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-primary hover:bg-primary/90">
            Guardar Cambios
          </Button>
       </DialogFooter>

       {/* Footer con logo Rutapp */}
       <div className="border-t border-border/30 mt-6 pt-4 flex flex-col items-center justify-center gap-2 pb-4">
         <img
           src={rutappLogo}
           alt="Rutapp"
           className="h-20 w-auto opacity-90"
         />
       </div>
       </DialogContent>

       {/* Modal de planes */}
       <PlanUpgradeModal
         isOpen={isPlanModalOpen}
         onClose={() => setIsPlanModalOpen(false)}
       />

       {/* Primera confirmación: informativa, no llama a ninguna callable */}
      <AlertDialog
        open={suspendConfirmStep === 'first'}
        onOpenChange={(open) => {
          if (!open) {
            setProfileIdToSuspend(null);
            setSuspendConfirmStep(null);
            setSuspendError('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este perfil?</AlertDialogTitle>
            <AlertDialogDescription>
              El perfil dejará de estar disponible, pero podrás recuperarlo durante 7 días.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setProfileIdToSuspend(null);
                setSuspendConfirmStep(null);
                setSuspendError('');
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setSuspendConfirmStep('second');
              }}
            >
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Segunda confirmación: definitiva, aquí se llama suspendProfile */}
      <AlertDialog
        open={suspendConfirmStep === 'second'}
        onOpenChange={(open) => {
          if (!open && !isSuspending) {
            setProfileIdToSuspend(null);
            setSuspendConfirmStep(null);
            setSuspendError('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirma la eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              {isSuspending
                ? 'Suspendiendo…'
                : 'Después de 7 días, el perfil y todos sus datos se eliminarán definitivamente. Durante ese plazo podrás restaurarlo desde Perfiles pendientes de eliminación.'}
            </AlertDialogDescription>
            {suspendError && (
              <p className="text-sm text-destructive mt-2">{suspendError}</p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isSuspending}
              onClick={() => {
                setProfileIdToSuspend(null);
                setSuspendConfirmStep(null);
                setSuspendError('');
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isSuspending}
              onClick={(e) => {
                e.preventDefault();
                handleSuspendProfile();
              }}
            >
              {isSuspending ? 'Eliminando…' : 'Eliminar perfil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Diálogo de confirmación para quitar el PIN de administrador */}
      <AlertDialog open={showRemoveAdminPinConfirm} onOpenChange={(open) => { if (!open) setShowRemoveAdminPinConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el PIN de Configuración?</AlertDialogTitle>
            <AlertDialogDescription>
              Podrás entrar en Ajustes generales sin introducir PIN.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemovingAdminPin}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleRemoveAdminPin}
              disabled={isRemovingAdminPin}
            >
              {isRemovingAdminPin ? 'Quitando…' : 'Quitar PIN'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmación para quitar el PIN de un perfil */}
      <AlertDialog open={profileIdToResetPin !== null} onOpenChange={(open) => { if (!open) setProfileIdToResetPin(null); }}>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el PIN de este perfil?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la protección por PIN y se revocarán todos los dispositivos confiables de este perfil. Después podrá entrar sin PIN y configurar uno nuevo cuando quiera.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                handleResetProfilePin(profileIdToResetPin);
                setProfileIdToResetPin(null);
              }}
            >
              Restablecer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmación para promocionar a Admin desde cada perfil */}
      <AlertDialog
        open={memberIdToPromote !== null}
        onOpenChange={(open) => {
          if (!open && !isProcessingMemberAction) {
            setMemberIdToPromote(null);
            setMemberActionError('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Convertir esta cuenta en Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Tendrá acceso completo al workspace, incluidos todos los perfiles, Configuración y la gestión de cuentas y roles.
            </AlertDialogDescription>
            {memberActionError && (
              <p className="text-sm text-destructive mt-2">{memberActionError}</p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessingMemberAction}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isProcessingMemberAction}
              onClick={(e) => {
                e.preventDefault();
                handlePromoteProfileMember(memberIdToPromote);
              }}
            >
              {isProcessingMemberAction ? 'Promoviendo…' : 'Convertir en Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmación para degradar de Admin desde cada perfil */}
      <AlertDialog
        open={memberIdToDemote !== null}
        onOpenChange={(open) => {
          if (!open && !isProcessingMemberAction) {
            setMemberIdToDemote(null);
            setMemberActionError('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el acceso Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              La cuenta volverá a tener acceso User a su perfil asignado. Ya no podrá acceder a Configuración ni gestionar roles.
            </AlertDialogDescription>
            {memberActionError && (
              <p className="text-sm text-destructive mt-2">{memberActionError}</p>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessingMemberAction}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isProcessingMemberAction}
              onClick={(e) => {
                e.preventDefault();
                handleDemoteAdminMember(memberIdToDemote);
              }}
            >
              {isProcessingMemberAction ? 'Degradando…' : 'Quitar Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmación para cerrar sesión */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={(open) => { if (!open) setShowLogoutConfirm(false); }}>


        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cierre de sesión</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres cerrar sesión?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { setShowLogoutConfirm(false); handleLogout(); }}
            >
              Sí, cerrar sesión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
